<template>
	<header-block :title="title" :border="image ? 0 : null" :bgcolor="image ? '#281C3Bff' : 'white'" :color="image ? 'white' : null"></header-block>
	<div class="shop-voucher-page">
		<!-- Cart Basket (shown when cart has items) -->
		<div class="cart-basket-section mb-4" v-if="cartVoucherCount > 0">
			<VoucherBasket 
				:voucherCount="cartVoucherCount"
				:totalAmount="cartTotalAmount"
				:basketText="cartVoucherCount > 1 ? 'Shopping Basket' : 'Pick up where you left off'"
				colour="#C6CAE5"
				:icon="cartIcon"
				@click="handleCartBasketClick" 
			/>
		</div>

		<transition>
			<div class="head" v-if="filter && availableVouchers">
				<div class="header" :style="'background-image: url(' + image + ')'">
					<v-row>
						<v-col class="v-col-7 title-col">
							<h1>{{ title }}</h1>
						</v-col>
						<v-col class="v-col-5 chip-col">
							<v-chip color="white" variant="flat">{{ filterCount }} card(s)</v-chip>
						</v-col>
					</v-row>
				</div>
			</div>
		</transition>

		<v-row class="mb-4" v-if="!filter">
			<v-col class="search-col">
				<v-text-field
					ref="search"
					v-model="query"
					prepend-inner-icon="mdi-magnify"
					density="compact"
					label="Search all vouchers"
					variant="solo"
					:flat="true"
					hide-details
					clearable
					single-line
					bg-color="#eeeeee"
					@click="gotoSearch"
				></v-text-field>
			</v-col>
		</v-row>

		<v-row class="topMenuRow">
			<v-col class="topMenuCol">
				<v-btn :class="['topTabButton', { selected: !forYou }]" title="" @click="tabClicked(false)">Featured</v-btn>
			</v-col>
			<v-col class="topMenuCol">
				<v-btn :class="['topTabButton', { selected: forYou }]" title="" @click="tabClicked(true)">For you</v-btn>
			</v-col>
		</v-row>

		<!-- FEATURED -->
		<div v-if="!forYou">
			<SectionHeader
				v-if="activeCategories && activeCategories.length > 0" 
				class="mt-1"
				title="Categories" 
				subtitle="Discover how you can save" 
				actionLabel="View all"
				@action="onCategorySectionHeaderAction"
			/>
			<div class="container" v-if="activeCategories && activeCategories.length > 0">
				<SmoothScrollContainer>
					<div :class="'smoothScrollContainerItem'" v-for="(category, index) in this.activeCategories" :key="category.id">
						<CategoryButton
							:key="index"
							:title="category.buttonTitle"
							:image="category.buttonImage"
							@click="categoryClicked({ title: category.pageTitle, filters: category.providerFilters, image: category.pageImage });"
						/>
					</div>
				</SmoothScrollContainer>
			</div>
			<div class="container" v-else>
				<SmoothScrollContainer >
					<div :class="'smoothScrollContainerItem'" v-for="i in 4" :key="i">
						<v-skeleton-loader
							type="image, article"
							height="220"
							class="mr-2"
						></v-skeleton-loader>
					</div>
				</SmoothScrollContainer>
			</div>

			<v-skeleton-loader v-if="!boostedVouchers" type="list-item-two-line" height="80" class="mb-6"></v-skeleton-loader>
			<MGNavPanel
				v-else-if="boostedVouchers && boostedVouchers.length > 0" 
				title="Boosted" 
				subtitle="Save whilst they last" 
				:icon="require('@/assets/lightning-bolt.png')"
				:background="require('@/assets/bg/boosted.png')"
				@click="onBoostedClick" class="mb-6" />

			<SwipeCarousel v-if="vouchersSummary && (vouchersSummary.allTimeSavings ?? 0) == 0"
				:showArrows="false" 
				:showButtons="false" 
				:paginationDots="true" 
				:itemCount="2"
				:height="'300px'"
				class="howItWorksCarousel">
				<v-carousel-item>
					<BuyWithConfidenceShoutPanel @click="$router.push('/BuyWithConfidence')" />
				</v-carousel-item>
				<v-carousel-item>
					<HowItWorksVoucherPanel @click="$router.push('/HowItWorksVouchers')" />
				</v-carousel-item>
			</SwipeCarousel>

			<SectionHeader v-if="topSavingsVouchersThisMonth && topSavingsVouchersThisMonth.length > 0"
				class="sectionHeader"
				title="Top Savings" 
				subtitle="Explore the best deals this month" 
				actionLabel="View all"
				@action="onTopSavingsSectionHeaderAction"
			/>
			<div class="container" v-if="topSavingsVouchersThisMonth && topSavingsVouchersThisMonth.length > 0">
			<SmoothScrollContainer>
				<div :class="'smoothScrollContainerItem'" v-for="(v, index) in topSavingsVouchersThisMonth" :key="v.id">
					<VoucherRecommendedForYou
						:key="index"
						:imageUrl="v.merchant.category?.buttonImage"
						:isFavorited="v.favourite"
						:merchantLogoUrl="v.cardUrl"
						:merchantName="v.merchant.name"
						:savingsPercentage="v.effectiveDiscountPercentage"
						:small="true"
						@favorite-clicked="() => { favouriteToggled(v); }"
						@click="voucherClicked(v)"
						:formatter="formatter"
					/>
				</div>
			</SmoothScrollContainer>
			</div>
			<div class="container" v-else>
				<SmoothScrollContainer>
					<div :class="'smoothScrollContainerItem'" v-for="i in 4" :key="i">
						<v-skeleton-loader
							type="image, article"
							height="220"
							class="mr-2"
						></v-skeleton-loader>
					</div>
				</SmoothScrollContainer>
			</div>

			<SectionHeader v-if="featuredVouchers && featuredVouchers.length > 0"
				title="Featured Discounts" 
				subtitle="Explore our featured discounts" 
				actionLabel="View all"
				@action="onFeaturedSectionHeaderAction"
				class="sectionHeader"
			/>
			<div class="container" v-if="featuredVouchers && featuredVouchers.length > 0">
				<SmoothScrollContainer>
					<div :class="'smoothScrollContainerItem'" v-for="(card, index) in featuredVouchers" :key="index">
						<VoucherMerchantCard
							:logoUrl="card.cardUrl"
							:merchantName="card.merchant.name"
							:savingPercentage="card.effectiveDiscountPercentage"
							@favorite-toggled="() => { favouriteToggled(card); }"
							@click="voucherClicked(card)"
							:isFavorited="card.favourite"
						/>
					</div>
				</SmoothScrollContainer>
			</div>
			<div class="container" v-else>
				<SmoothScrollContainer>
					<div :class="'smoothScrollContainerItem'" v-for="i in 4" :key="i">
						<v-skeleton-loader
							type="image, article"
							height="220"
							class="mr-2"
						></v-skeleton-loader>
					</div>
				</SmoothScrollContainer>
			</div>
			<SwipeCarousel v-if="vouchersSummary && (vouchersSummary.allTimeSavings ?? 0) > 0"
				:showArrows="false" 
				:showButtons="false" 
				:paginationDots="true" 
				:itemCount="2"
				:height="'300px'"
				class="howItWorksCarousel">
				<v-carousel-item>
					<BuyWithConfidenceShoutPanel @click="$router.push('/BuyWithConfidence')" />
				</v-carousel-item>
				<v-carousel-item>
					<HowItWorksVoucherPanel @click="$router.push('/HowItWorksVouchers')" />
				</v-carousel-item>
			</SwipeCarousel>

		</div>
		<!-- FOR YOU -->
		<div v-else>
			<div v-if="bankInfo && bankInfo.accounts.length == 0">
				<ReLinkAccountsForVouchersShoutPanel />
			</div>
			<div v-else>
				<div> <!-- Biggest Discounts Start -->
					<SectionHeader
						title="Biggest Discounts" 
						subtitle="Based on your spending in the last 6 months" 
						:actionLabel="biggestDiscounts6Months && biggestDiscounts6Months.length > 0 ? 'View all' : null"
						@action="onBiggestDiscountsSectionHeaderAction"
						class="mt-1"
					/>
					<div class="container" v-if="biggestDiscounts6Months && biggestDiscounts6Months.length > 0">
						<SmoothScrollContainer>
							<div :class="'smoothScrollContainerItem'" v-for="(v, index) in biggestDiscounts6Months" :key="v.id">
								<VoucherRecommendedForYou 
									:key="index"
									:imageUrl="v.discountCategory.buttonImage"
									:isFavorited="v.favourite"
									:merchantLogoUrl="v.cardUrl"
									:merchantName="v.merchant.name"
									:savingsAmount="v.saving"
									:savingsPercentage="v.effectiveDiscountPercentage"
									:small="true"
									@favorite-clicked="() => { favouriteToggled(v); }"
									@click="voucherClicked(v)"
									:formatter="formatter"
								/>
							</div>
						</SmoothScrollContainer>
					</div>
					<div class="container" v-else-if="biggestDiscounts6Months == null">
						<SmoothScrollContainer>
							<div :class="'smoothScrollContainerItem'" v-for="i in 4" :key="i">
								<v-skeleton-loader
									type="image, article"
									height="220"
									class="mr-2"
								></v-skeleton-loader>
							</div>
						</SmoothScrollContainer>
					</div>
					<div v-else>
						<div class="noDiscounts" v-if="!filter">
							<img class="mt-4" src="@/assets/figureofeight.png"/>
							<div class="mt-4"><p><b>We couldn't see enough recent activity to tailor offers to you.</b></p>
							Connect a regularly used account to unlock smarter suggestions.
							</div>
							
						</div>
					</div>
				</div> <!-- Biggest Discounts End -->

				<div> <!-- Where you could have saved Start-->
					<SectionHeader
						title="Where you could have saved" 
						subtitle="Based on your spending in the last 6 months" 
						@action="onWhereYouCouldHaveSavedSectionHeaderAction"
						class="sectionHeader"
					/>
					<div class="container" v-if="recommendedVouchers6Months">
						<SmoothScrollContainer>
							<div :class="'smoothScrollContainerItem'" >
								<VoucherSavingSuggestions
									:key="1"
									:labelValue="isNaN(topMerchantSpend[0]?.spent) ? formatter.format(0) : formatter.format(Math.abs(topMerchantSpend[0]?.spent))"
									label="Total spend"
									:logoUrl="topMerchantSpend[0]?.merchant.logoUrl"
									:savingAmount="isNaN(topMerchantSpend[0]?.saving) ? formatter.format(0) : formatter.format(topMerchantSpend[0]?.saving)"
									:savingPercentage="isNaN(topMerchantSpend[0]?.effectiveDiscountPercentage) ? 0 : topMerchantSpend[0]?.effectiveDiscountPercentage"
									@click="onTopMerchantSpendClick"
								/>
							</div>
							<div :class="'smoothScrollContainerItem'" >
								<VoucherSavingSuggestions
									:key="2"
									label="Biggest single saving"
									:logoUrl="topMerchantSaving[0]?.merchant.logoUrl"
									:savingAmount="isNaN(topMerchantSaving[0]?.saving) ? formatter.format(0) : formatter.format(topMerchantSaving[0]?.saving)"
									:savingPercentage="isNaN(topMerchantSaving[0]?.effectiveDiscountPercentage) ? 0 : topMerchantSaving[0]?.effectiveDiscountPercentage"
									@click="onTopMerchantSavingClick"
								/>
							</div>

							<div :class="'smoothScrollContainerItem'" >
								<VoucherSavingSuggestions
									:key="3"
									label="Biggest discount"
									:logoUrl="topMerchantDiscount[0]?.merchant.logoUrl"
									:savingAmount="isNaN(topMerchantDiscount[0]?.saving) ? formatter.format(0) : formatter.format(topMerchantDiscount[0]?.saving)"
									:savingPercentage="isNaN(topMerchantDiscount[0]?.effectiveDiscountPercentage) ? 0 : topMerchantDiscount[0]?.effectiveDiscountPercentage"
									@click="onTopMerchantDiscountClick"
								/>
							</div>
							<div :class="'smoothScrollContainerItem'" >
								<VoucherSavingSuggestions
									:key="4"
									label="Most frequent"
									:labelValue="topMerchantUsed[0]?.transactionCount ?? 0"
									:logoUrl="topMerchantUsed[0]?.merchant.logoUrl"
									:savingAmount="isNaN(topMerchantUsed[0]?.saving) ? formatter.format(0) : formatter.format(topMerchantUsed[0]?.saving)"
									:savingPercentage="isNaN(topMerchantUsed[0]?.effectiveDiscountPercentage) ? 0 : topMerchantUsed[0]?.effectiveDiscountPercentage"
									@click="onTopMerchantUsedClick"
								/>
							</div>
						</SmoothScrollContainer>
					</div>
					<div class="container" v-else>
						<SmoothScrollContainer>
							<div :class="'smoothScrollContainerItem'" v-for="i in 4" :key="i">
								<v-skeleton-loader
									type="image, article"
									height="220"
									class="mr-2"
								></v-skeleton-loader>
							</div>
						</SmoothScrollContainer>
					</div>
				</div> <!-- Where you could have saved End-->

				<div v-if="recommendedVouchersLastMonth && recommendedVouchersLastMonth.length > 0"> <!-- Recommended Start -->
					<SectionHeader
						title="Your spending last month" 
						:subtitle="`Based on where you shopped in ${periodString}`" 
						actionLabel="View all"
						@action="onRecommendedSectionHeaderAction"
						class="sectionHeader"
					/>
					<div class="recommendedCarousel">
						<SwipeCarousel
							:showArrows="false" 
							:showButtons="false" 
							:paginationDots="true" 
							:itemCount="recommendedVouchersLastMonth?.length || 1"
							:height="'370px'">	
							<v-carousel-item v-for="(v, index) in recommendedVouchersLastMonth" :key="v.id">
								<VoucherRecommendedForYou 
									:key="index"
									:category="v.discountCategory.pageTitle"
									:imageUrl="v.discountCategory.buttonImage"
									:isFavorited="v.favourite"
									:merchantLogoUrl="v.merchant.logoUrl"
									:merchantName="v.merchant.name"
									:savingsAmount="calculateSavingAmount(v)"
									:savingsPercentage="v.effectiveDiscountPercentage"
									cardLabel="Where you spent the most"
									:spendAmount="calculateSpendAmount(v)"
									:spendLabel="calculateSpendLabel(v)"
									@favorite-clicked="() => { favouriteToggled(v); }"
									@click="voucherClicked(v)"
									:formatter="formatter"
								/>
							</v-carousel-item>
						</SwipeCarousel>
					</div>
				</div>
				<div class="mb-8" v-else-if="recommendedVouchersLastMonth == null">
					<SwipeCarousel
						:showArrows="false" 
						:showButtons="false" 
						:paginationDots="true" 
						:height="'360px'">
						<div>
							<v-skeleton-loader
								type="card"
								height="220"
								class="mr-2"
							></v-skeleton-loader>
						</div>
					</SwipeCarousel>
				</div> <!-- Recommended End -->
			</div>

		</div>
	</div>
	<div style="clear:both; "></div>	
</template>

<script>
import Services from '../mixins/Services';
import Helpers from '../mixins/Helpers';
import HeaderBlock from './elements/HeaderBlock.vue';
import CategoryButton from './elements/CategoryButton.vue';
import SectionHeader from './elements/SectionHeader.vue';
import SmoothScrollContainer from './elements/SmoothScrollContainer.vue';
import MGNavPanel from './elements/MGNavPanel.vue';
import BuyWithConfidenceShoutPanel from './elements/BuyWithConfidenceShoutPanel.vue';
import HowItWorksVoucherPanel from './elements/HowItWorksVoucherShoutPanel.vue';
import VoucherRecommendedForYou from './elements/VoucherRecommendedForYou.vue';
import VoucherMerchantCard from './elements/VoucherMerchantCard.vue';
import SwipeCarousel from './elements/SwipeCarousel.vue';
import VoucherSavingSuggestions from './elements/VoucherSavingSuggestions.vue';
import ReLinkAccountsForVouchersShoutPanel from './elements/ReLinkAccountsForVouchersShoutPanel.vue';
import VoucherBasket from './elements/VoucherBasket.vue';

export default {
	mixins: [Services, Helpers],
	components: {HeaderBlock, 
		SectionHeader, 
		SmoothScrollContainer, CategoryButton,
		MGNavPanel, BuyWithConfidenceShoutPanel, 
		VoucherRecommendedForYou, VoucherMerchantCard, VoucherSavingSuggestions,
		SwipeCarousel, ReLinkAccountsForVouchersShoutPanel, HowItWorksVoucherPanel,
		VoucherBasket
	},
	data() {
		return {
			query: null,
			filter: this.$route.query.filters ?? null,
			filterCount: 0,
			title: this.$route.query.title ?? "Shop Vouchers",
			image: this.$route.query.image ?? null,
			favouriteVouchers: null,
			topSavingsVouchersThisMonth: null,
			biggestDiscounts6Months: null,
			biggestSavings3Months: null,
			featuredVouchers: null,
			boostedVouchers: null,
			forYou: false,
			recommendedVouchers6Months: null,
			recommendedVouchers3Months: null,
			recommendedVouchers1Month: null,
			recommendedVouchersLastMonth: null,
			topMerchantSpend: null,
			topMerchantSaving: null,
			topMerchantDiscount: null,
			topMerchantUsed: null,
			activeCategories: null,
			cartItems: [],
		}
	},
	computed: {
		cartIcon() {
			if (this.cartItems.length == 1) {
				console.log('Cart icon URL:', this.cartItems[0]);
				return this.cartItems[0].availableVoucher.cardUrl;
			} else {
				return '@/assets/basket.svg';
			}
		},
		hasCartItems() {
			return this.cartItems && this.cartItems.length > 0;
		},
		cartVoucherCount() {
			return this.cartItems ? this.cartItems.length : 0;
		},
		cartTotalAmount() {
			if (!this.cartItems || this.cartItems.length === 0) return 0;
			return this.cartItems.reduce((total, item) => {
				return total + (parseFloat(item.amount) || 0);
			}, 0);
		},
	},
	methods: {
		onLoad: async function() {
			if (this.$route.query.recommended !== undefined) {
				this.forYou = this.$route.query.recommended === 'true';
			}
			// Check for cart in session storage
			this._getCart((r) => {
				console.log('Cart items retrieved:', r);
				if (r && r.length > 0) {
					this.cartItems = r;
				}
			});
			this._getCategories(() => {
				this.activeCategories = this.categories.filter(c => c.active);
			});
			this._getCustomer(() => {
				this._getCustomerVouchersSummary();
				this._getBankingInfo();
			});
			this._getAvailableVouchers(() => {
				this.favouriteVouchers = this.availableVouchers.filter(v => v.favourite);
				this.featuredVouchers = this.availableVouchers.filter(v => v.featured);
				this.boostedVouchers = this.availableVouchers.filter(v => v.manualOverride);
				this.availableVouchers.forEach(v => {
					this._removeUnavailableDiscountProviders(v);
				});
				this.topSavingsVouchersThisMonth = this.topSavings(this.availableVouchers);
			});
			this._getRecommendedVouchers(-180, (r) => {
				this.recommendedVouchers6Months = this._deepCopy(r);
				this.biggestDiscounts6Months = this.topDiscounts(this.recommendedVouchers6Months);
				this.recommendedVouchersLastMonth = this.recommendedVouchers6Months.filter(v => {
						return v.lastMonthSpend && Math.abs(v.lastMonthSpend) > 0;
					}).slice(0, 3);
				const lastMonth = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
				this.periodString = lastMonth.toLocaleString('default', { month: 'long' });
				this.topMerchantSpend = [...this.recommendedVouchers6Months].sort((a, b) => (Math.abs(b.spent || 0) - Math.abs(a.spent || 0))).slice(0, 1);
				this.topMerchantSaving = [...this.recommendedVouchers6Months].sort((a, b) => (Math.abs(b.biggestSaving || 0) - Math.abs(a.biggestSaving || 0))).slice(0, 1);
				this.topMerchantDiscount = [...this.recommendedVouchers6Months].sort((a, b) => (b.effectiveDiscountPercentage || 0) - (a.effectiveDiscountPercentage || 0)).slice(0, 1);
				this.topMerchantUsed = [...this.recommendedVouchers6Months].sort((a, b) => (b.transactionCount || 0) - (a.transactionCount || 0)).slice(0, 1);
			});
			this._getRecommendedVouchers(-90, (r) => {
				this.recommendedVouchers3Months = this._deepCopy(r);
				this.biggestSavings3Months = this.topSavings(this.recommendedVouchers3Months);
			});
			this._getRecommendedVouchers(-30, (r) => {
				this.recommendedVouchers1Month = this._deepCopy(r);
			});
			this._getCustomerVouchers();
		},
		topSavings(vouchers) {
			var top = vouchers.sort((a, b) => {
				return (b.effectiveDiscountPercentage || 0) - (a.effectiveDiscountPercentage || 0);
			});
			return top.slice(0, 10);
		},
		topDiscounts(vouchers) {
			var top = vouchers.sort((a, b) => {
				return (b.saving || 0) - (a.saving || 0);
			});
			console.log('top discounts', top);
			return top.slice(0, 10);
		},
		onTopSavingsSectionHeaderAction() {
			this.$router.push({ path: '/TopSavingsVouchers' });
		},
		onCategorySectionHeaderAction() {
			this.$router.push({ path: '/VoucherCategories' });
		},
		onWhereYouCouldHaveSavedSectionHeaderAction() {
			alert('Where you could have saved section header action clicked!');
		},
		onFeaturedSectionHeaderAction() {
			this.$router.push({ path: '/FeaturedVouchers' });
		},
		onRecommendedSectionHeaderAction() {
			this.$router.push({ path: '/RecommendedVouchers' });
		},
		onBiggestDiscountsSectionHeaderAction() {
			if (this.biggestDiscounts6Months && this.biggestDiscounts6Months.length > 0) {
				this.$router.push({ path: '/BiggestDiscountsVouchers' });
			}
		},
		onBiggestSavingsSectionHeaderAction() {
			this.$router.push({ path: '/BiggestSavingsVouchers' });
		},
		onBoostedClick() {
			this.$router.push({ path: '/BoostedVouchers' });
		},
		categoryClicked: function(o) {
			this.$router.push({ path: '/vouchers', query: o });
		},
		voucherClicked: function(voucher) {
			this.$router.push({ path: '/vouchers/amount/' + voucher.id });
		},
		onTopMerchantSpendClick() {
			if (!isNaN(this.topMerchantSpend[0]?.spent) && Math.abs(this.topMerchantSpend[0]?.spent) > 0) {
				this.voucherClicked(this.topMerchantSpend[0]);
			} else {
				this.$router.push({ path: '/wallet' });
			}
		},
		onTopMerchantSavingClick() {
			if (!isNaN(this.topMerchantSaving[0]?.spent) && Math.abs(this.topMerchantSaving[0]?.spent) > 0) {
				this.voucherClicked(this.topMerchantSaving[0]);
			} else {
				this.$router.push({ path: '/wallet' });
			}
		},
		onTopMerchantDiscountClick() {
			if (!isNaN(this.topMerchantDiscount[0]?.spent) && Math.abs(this.topMerchantDiscount[0]?.spent) > 0) {
				this.voucherClicked(this.topMerchantDiscount[0]);
			} else {
				this.$router.push({ path: '/wallet' });
			}
		},
		onTopMerchantUsedClick() {
			if (!isNaN(this.topMerchantUsed[0]?.spent) && Math.abs(this.topMerchantUsed[0]?.spent) > 0) {
				this.voucherClicked(this.topMerchantUsed[0]);
			} else {
				this.$router.push({ path: '/wallet' });
			}
		},
		calculateSavingAmount(voucher) {
			if (voucher.lastMonthSpend && voucher.effectiveDiscountPercentage) {
				return (Math.abs(voucher.lastMonthSpend) * (voucher.effectiveDiscountPercentage / 100));
			}
			return 0;
		},
		calculateSpendAmount(voucher) {
			return isNaN(voucher.lastMonthSpend) ? this.formatter.format(0) : this.formatter.format(Math.abs(voucher.lastMonthSpend));
		},
		calculateSpendLabel() {
			const lastMonth = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
			const monthString = lastMonth.toLocaleString('default', { month: 'long' });
			return `${monthString} spend`;
		},
		tabClicked: function(isForYou) {
			this.forYou = isForYou;
			this.$router.replace({ 
				path: this.$route.path, 
				query: { 
					...this.$route.query, 
					recommended: isForYou 
				} 
			});
		},
		gotoSearch: function() {
			this.$router.push({ path: '/vouchers/search' });
		},
		favouriteToggled: function(voucher) {
			if (voucher.favourite) {
				this._removeCustomerFavouriteDiscount(voucher, () => {
					voucher.favourite = !voucher.favourite;
				});
			} else {
				this._addCustomerFavouriteDiscount(voucher, () => {
					voucher.favourite = !voucher.favourite;
				});
			}
		},
		handleCartBasketClick() {
			console.log('Cart basket clicked, navigating to cart...');
			// Navigate to the cart page
			this.$router.push('/VoucherCart');
		},
	},
	mounted: function () {
		this.onLoad();
	},
}
</script>

<style scoped>

.shop-voucher-page {
	min-height: 100vh;
	display: flex;
	flex-direction: column;
}

.howItWorksCarousel {
	min-height: 335px;
}

.header {
	padding-top: 138px;
	background-size: cover;
	background-position: bottom;
	margin: -20px -30px 0px -30px;
	margin-top: -138px;
	color: white;
	padding-bottom: 200px;
	height: 300px;
	padding: 30px;
	padding-top: 200px;
	color: white;
	h1{
		font-size: 28px;
		margin-bottom: 15px;
	}
	margin-bottom: 40px;
}
.title-col {
	padding-top: 0px;
	padding-bottom: 20px;
}
.chip-col {
	text-align: right;
}

.topTabButton
{
	margin-top: 0px;
	text-transform: none;
	height: 40px !important;
	padding-top: 0px !important;
	padding-bottom: 0px !important;
	vertical-align: middle !important;
	background-color: #ffffff66 !important;
	font-weight: bold;
}

.topTabButton.selected {
    background-color: var(--text-color) !important;
	color: white !important;
}

.topMenuCol {
	padding-left: 3px;
	padding-right: 3px;
	margin-top: 0px;
	padding-top: 0px;
}

.topMenuRow
{
	margin-left: -5px;
	margin-right: -5px;
	margin-top: 0px;
	margin-bottom: 0px;
}

.container {
	width: 100vw;
	max-width: 100vw;
	margin-left: -30px;
	padding-left: 30px;
	padding-right: 30px;
	margin-right: -0px;
	overflow-x: hidden;
	margin-top: -20px;
	margin-bottom: 0px;
}

.recommended {
	margin-top: -20px;
	margin-bottom: 0px;
}

.recommendedCarousel {
	margin-top: -20px !important;
	min-height: 400px;
}

.topVouchers {
	margin-top: -20px;
	margin-bottom: 0px;
}

.noDiscounts {
	background-color: #E9E0DE;
	border-radius: 8px;
	padding: 20px;
	margin-bottom: 20px;
	text-align: center;
}

.sectionHeader {
	margin-top: 12px;
}

.cart-basket-section {
	margin-top: -18px;
	margin-right: -30px;
	margin-left: -30px;
}

</style>
